import React, { useEffect, useState } from 'react'
import { Alert, Button, Flex, Input, Segmented, Table, Tag, Tooltip, Typography } from 'antd'
import { CheckCircleOutlined, ExportOutlined, SettingOutlined, WarningOutlined } from '@ant-design/icons'
import { useAuth } from '../../auth'
import { STORE_CAMPAIGN } from '../../user/permission'
import { ScreeningDrawer } from './screening-drawer'
import { Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { VacancyFilters } from '../api'
import { useSiteOverview, useSiteVacancies } from '../hooks'
import { SiteVacancy } from '../site'

export type SiteVacanciesProps = {
    siteId: string
}

/**
 * The website's vacancies, about what screening brings in: candidates per vacancy, and whether the apply button works.
 */
export function SiteVacancies({ siteId }: SiteVacanciesProps) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [screening, setScreening] = useState<SiteVacancy | null>(null)

    // Open vacancies by default, so a new website shows what Evaa found instead of an empty WhatsApp list.
    const [filters, setFilters] = useState<VacancyFilters>({
        filter: 'open',
        page: 1,
    })

    const [search, setSearch] = useState('')
    const { data, isLoading, isFetching } = useSiteVacancies(siteId, filters)
    const { data: overview } = useSiteOverview(siteId)
    const whatsappMissing = overview?.todos.some((todo) => todo.key === 'whatsapp_not_configured') ?? false

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters((current) => ({
                ...current,
                search: search || undefined,
                page: 1,
            }))
        }, 400)

        return () => clearTimeout(timer)
    }, [search])

    const columns = [
        {
            title: t('Vacancy'),
            key: 'title',
            render: (vacancy: SiteVacancy) => <VacancyTitleCell vacancy={vacancy} />,
        },
        {
            title: t('Via WhatsApp'),
            key: 'screening',
            width: 140,
            render: (vacancy: SiteVacancy) => (
                <ScreeningCell
                    vacancy={vacancy}
                    onSwitchOn={() => setScreening(vacancy)}
                />
            ),
        },
        {
            title: t('Candidates'),
            key: 'candidates',
            width: 150,
            render: (vacancy: SiteVacancy) => <CandidatesCell vacancy={vacancy} />,
        },
        {
            title: t('Apply button'),
            key: 'button',
            width: 120,
            render: (vacancy: SiteVacancy) => <ApplyButtonCell vacancy={vacancy} />,
        },
        {
            title: '',
            key: 'actions',
            width: 90,
            render: (vacancy: SiteVacancy) => <VacancyActionsCell vacancy={vacancy} />,
        },
    ]

    const openBoard = (vacancy: SiteVacancy) => {
        if (vacancy.campaignId) {
            navigate({
                to: '/campaigns/$campaignId/board',
                params: { campaignId: vacancy.campaignId },
            })
        }
    }

    const emptyText =
        filters.filter === 'agent'
            ? t('No vacancies with applying via WhatsApp yet. Choose "Open" and switch it on per vacancy.')
            : t('No vacancies found')

    return (
        <Flex
            vertical
            gap="middle"
        >
            {whatsappMissing && (
                <Alert
                    type="warning"
                    showIcon
                    message={t('Choose the WhatsApp number and start message first, then switch vacancies on.')}
                    action={
                        <Link
                            to="/websites/$siteId/settings/whatsapp"
                            params={{ siteId }}
                        >
                            {t('Open settings')}
                        </Link>
                    }
                />
            )}
            <Flex
                gap="small"
                wrap
                align="center"
            >
                <Input.Search
                    allowClear
                    placeholder={t('Search by title or place')}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="max-w-xs"
                />
                <Segmented
                    value={filters.filter ?? 'open'}
                    onChange={(value) =>
                        setFilters({
                            ...filters,
                            filter: value as VacancyFilters['filter'],
                            page: 1,
                        })
                    }
                    options={[
                        {
                            label: t('Via WhatsApp'),
                            value: 'agent',
                        },
                        {
                            label: t('Open'),
                            value: 'open',
                        },
                        {
                            label: t('All'),
                            value: 'all',
                        },
                    ]}
                />
            </Flex>
            <Table
                dataSource={data?.data ?? []}
                columns={columns}
                rowKey="id"
                loading={isLoading || isFetching}
                size="middle"
                scroll={{ x: 640 }}
                rowClassName={(vacancy) => (vacancy.campaignId ? 'cursor-pointer' : '')}
                onRow={(vacancy) => ({ onClick: () => openBoard(vacancy) })}
                pagination={{
                    current: data?.meta.currentPage,
                    total: data?.meta.total,
                    pageSize: data?.meta.perPage,
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                    onChange: (page) => setFilters({ ...filters, page }),
                }}
                locale={{ emptyText }}
            />
            {screening && (
                <ScreeningDrawer
                    siteId={siteId}
                    vacancy={screening}
                    onClose={() => setScreening(null)}
                />
            )}
        </Flex>
    )
}

type VacancyCellProps = {
    vacancy: SiteVacancy
}

function NoValue() {
    return <Typography.Text type="secondary">–</Typography.Text>
}

function VacancyTitleCell({ vacancy }: VacancyCellProps) {
    const { t } = useTranslation()
    const details = [vacancy.city, vacancy.status === 'closed' ? t('Closed') : null]

    return (
        <Flex vertical>
            <Typography.Text strong={Boolean(vacancy.campaignId)}>{vacancy.title}</Typography.Text>
            <Typography.Text
                type="secondary"
                className="text-xs"
            >
                {details.filter(Boolean).join(' · ')}
            </Typography.Text>
        </Flex>
    )
}

type ScreeningCellProps = VacancyCellProps & {
    onSwitchOn: () => void
}

function ScreeningCell({ vacancy, onSwitchOn }: ScreeningCellProps) {
    const { t } = useTranslation()
    const { can } = useAuth()

    if (vacancy.campaignId) {
        return <Tag color={vacancy.applyUrl ? 'green' : 'default'}>{vacancy.applyUrl ? t('Active') : t('Paused')}</Tag>
    }

    if (vacancy.status !== 'open' || !can(STORE_CAMPAIGN)) {
        return <NoValue />
    }

    return (
        <Button
            size="small"
            onClick={(event) => {
                event.stopPropagation()
                onSwitchOn()
            }}
        >
            {t('Switch on')}
        </Button>
    )
}

function CandidatesCell({ vacancy }: VacancyCellProps) {
    const { t } = useTranslation()

    if (!vacancy.candidates) {
        return <NoValue />
    }

    return (
        <Flex
            gap="small"
            align="baseline"
        >
            <Typography.Text className="text-base font-medium">{vacancy.candidates.total}</Typography.Text>
            {vacancy.candidates.recent > 0 && (
                <Typography.Text
                    type="success"
                    className="text-xs"
                >
                    {t('+{{count}} this week', { count: vacancy.candidates.recent })}
                </Typography.Text>
            )}
        </Flex>
    )
}

function ApplyButtonCell({ vacancy }: VacancyCellProps) {
    const { t } = useTranslation()

    if (!vacancy.campaignId) {
        return null
    }

    if (vacancy.applyButtonFound === true) {
        return (
            <Tooltip title={t('Apply button found: "{{label}}"', { label: vacancy.applyButtonLabel ?? '' })}>
                <CheckCircleOutlined className="text-green-600" />
            </Tooltip>
        )
    }

    if (vacancy.applyButtonFound === false) {
        return (
            <Tooltip title={t('No apply button on the page; Evaa shows one under the vacancy text')}>
                <WarningOutlined className="text-orange-500" />
            </Tooltip>
        )
    }

    return <NoValue />
}

function VacancyActionsCell({ vacancy }: VacancyCellProps) {
    const { t } = useTranslation()

    return (
        <Flex
            gap={4}
            onClick={(event) => event.stopPropagation()}
        >
            {vacancy.url && (
                <Tooltip title={t('Open the vacancy')}>
                    <Button
                        type="text"
                        size="small"
                        icon={<ExportOutlined />}
                        href={vacancy.url}
                        target="_blank"
                    />
                </Tooltip>
            )}
            {vacancy.campaignId && (
                <Tooltip title={t('WhatsApp conversation settings')}>
                    <Link
                        to="/campaigns/$campaignId/settings"
                        params={{ campaignId: vacancy.campaignId }}
                    >
                        <Button
                            type="text"
                            size="small"
                            icon={<SettingOutlined />}
                        />
                    </Link>
                </Tooltip>
            )}
        </Flex>
    )
}
